import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { useMediators } from '@/hooks/useMediators';
import { useMediatorsWithFutureSlots } from '@/hooks/useMediatorsWithFutureSlots';
import { Search, User, Loader2, Star, Lock, Filter } from 'lucide-react';
import { ACTIVE_MEDIATION_AREAS, getMediationAreaLabel } from '@/lib/mediationAreaMapping';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MediatorResultCard } from '@/components/MediatorResultCard';

interface MediatorSearchWidgetProps {
  defaultMediationArea?: string;
}

const WEEK_DAYS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const WEEK_DAY_LABELS: Record<string, string> = {
  lundi: 'Lun', mardi: 'Mar', mercredi: 'Mer', jeudi: 'Jeu', vendredi: 'Ven', samedi: 'Sam',
};

export const MediatorSearchWidget = ({ defaultMediationArea = '' }: MediatorSearchWidgetProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchFilters, setSearchFilters] = useState({
    city: '',
    mediationArea: defaultMediationArea,
  });
  const [availabilityFilters, setAvailabilityFilters] = useState({
    selectedDates: [] as Date[],
    selectedWeekDays: [] as string[],
    timeOfDay: 'all' as 'all' | 'morning' | 'afternoon' | 'evening',
  });
  const [showAvailabilityFilters, setShowAvailabilityFilters] = useState(false);
  const [onlyWithSlots, setOnlyWithSlots] = useState(false);
  const [availableFirst, setAvailableFirst] = useState(true);

  const { data: mediators, isLoading: mediatorsLoading } = useMediators(searchFilters);
  const { data: mediatorsWithSlots, isLoading: slotsLoading } = useMediatorsWithFutureSlots();

  // Availability filtering (authenticated users only)
  const hasAvailabilityFilter = user && (
    availabilityFilters.selectedDates.length > 0 || 
    availabilityFilters.selectedWeekDays.length > 0 || 
    availabilityFilters.timeOfDay !== 'all'
  );

  const { data: searchSlots } = useQuery({
    queryKey: ['widgetSearchAvailabilitySlots', availabilityFilters],
    queryFn: async () => {
      let query = supabase
        .from('availability_slots')
        .select('mediator_id, date, start_time')
        .eq('is_booked', false)
        .gte('date', format(new Date(), 'yyyy-MM-dd'));

      if (availabilityFilters.selectedDates.length > 0) {
        const dateStrings = availabilityFilters.selectedDates.map(d => format(d, 'yyyy-MM-dd'));
        query = query.in('date', dateStrings);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!hasAvailabilityFilter,
  });

  const filteredMediators = (() => {
    if (!mediators) return [];

    let result = mediators;

    // Toggle "Afficher uniquement les médiateurs avec créneaux disponibles".
    if (onlyWithSlots && mediatorsWithSlots) {
      result = result.filter(m => m.id && mediatorsWithSlots.has(m.id));
    }

    if (!hasAvailabilityFilter || !searchSlots) return result;

    const matchingSlots = searchSlots.filter(slot => {
      if (availabilityFilters.selectedWeekDays.length > 0) {
        const slotDate = new Date(slot.date + 'T00:00:00');
        const dayName = format(slotDate, 'EEEE', { locale: fr }).toLowerCase();
        if (!availabilityFilters.selectedWeekDays.includes(dayName)) return false;
      }
      if (availabilityFilters.timeOfDay !== 'all') {
        const hour = parseInt(slot.start_time.split(':')[0], 10);
        if (availabilityFilters.timeOfDay === 'morning' && hour >= 12) return false;
        if (availabilityFilters.timeOfDay === 'afternoon' && (hour < 12 || hour >= 18)) return false;
        if (availabilityFilters.timeOfDay === 'evening' && hour < 18) return false;
      }
      return true;
    });

    const matchingMediatorIds = new Set(matchingSlots.map(s => s.mediator_id));
    return result.filter(m => m.id && matchingMediatorIds.has(m.id));
  })();

  const displayLimit = user ? 5 : 3;
  const anyFilterActive = hasAvailabilityFilter || onlyWithSlots;
  // Source list: filtered when any filter is active, otherwise the full list.
  const baseList = (anyFilterActive ? filteredMediators : mediators) ?? [];
  // Optional sort: mediators with at least one future free slot bubble up.
  const sortedList = (() => {
    if (!availableFirst || !mediatorsWithSlots) return baseList;
    return [...baseList].sort((a, b) => {
      const aHas = a.id && mediatorsWithSlots.has(a.id) ? 1 : 0;
      const bHas = b.id && mediatorsWithSlots.has(b.id) ? 1 : 0;
      return bHas - aHas;
    });
  })();
  const displayedMediators = sortedList.slice(0, displayLimit);
  const totalCount = baseList.length;

  return (
    <Card className="bg-card/95 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5" />
          Trouvez un médiateur
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Search Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="widget-city">Ville</Label>
            <Input
              id="widget-city"
              placeholder="Ex: Paris"
              value={searchFilters.city}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, city: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="widget-area">Domaine de médiation</Label>
            <Select
              value={searchFilters.mediationArea}
              onValueChange={(value) => setSearchFilters(prev => ({ ...prev, mediationArea: value === 'all' ? '' : value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Tous les domaines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les domaines</SelectItem>
                {ACTIVE_MEDIATION_AREAS.map((area) => (
                  <SelectItem key={area.value} value={area.value}>{area.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Quick toggle: only show mediators that have at least one future free slot. */}
        <div className="flex items-center justify-between rounded-md border p-3">
          <div className="flex-1 min-w-0 pr-3">
            <Label htmlFor="widget-only-with-slots" className="text-sm font-medium cursor-pointer">
              Afficher uniquement les médiateurs avec créneaux disponibles
            </Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Masque les profils sans aucune disponibilité publiée.
            </p>
          </div>
          <Switch
            id="widget-only-with-slots"
            checked={onlyWithSlots}
            onCheckedChange={setOnlyWithSlots}
          />
        </div>

        {/* Sort: bubble up mediators with future availability. Disabled when "only with slots" is on (redundant). */}
        <div className="flex items-center justify-between rounded-md border p-3">
          <div className="flex-1 min-w-0 pr-3">
            <Label htmlFor="widget-available-first" className="text-sm font-medium cursor-pointer">
              Trier : disponibles d'abord
            </Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Remonte les médiateurs ayant des créneaux futurs en haut de la liste.
            </p>
          </div>
          <Switch
            id="widget-available-first"
            checked={availableFirst}
            onCheckedChange={setAvailableFirst}
            disabled={onlyWithSlots}
          />
        </div>

        {/* Availability Filters - Only for authenticated users */}
        {user && (
          <div>
            <Button
              variant={showAvailabilityFilters ? 'default' : 'outline'}
              size="sm"
              className="w-full"
              onClick={() => setShowAvailabilityFilters(prev => !prev)}
            >
              <Filter className="h-4 w-4 mr-2" />
              {showAvailabilityFilters ? 'Masquer les filtres de disponibilité' : 'Filtrer par disponibilité'}
              {hasAvailabilityFilter && (
                <Badge variant="secondary" className="ml-2 text-xs">Actif</Badge>
              )}
            </Button>

            {showAvailabilityFilters && (
              <div className="space-y-4 pt-3 mt-3 border-t">
                {/* Specific dates */}
                <div>
                  <Label className="text-sm mb-2 block">Dates spécifiques</Label>
                  <Calendar
                    mode="multiple"
                    selected={availabilityFilters.selectedDates}
                    onSelect={(dates) => setAvailabilityFilters(prev => ({ ...prev, selectedDates: dates || [] }))}
                    disabled={(date) => date < new Date()}
                    className="rounded-md border pointer-events-auto"
                    locale={fr}
                  />
                  {availabilityFilters.selectedDates.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-1 text-xs"
                      onClick={() => setAvailabilityFilters(prev => ({ ...prev, selectedDates: [] }))}
                    >
                      Effacer les dates
                    </Button>
                  )}
                </div>

                {/* Week days */}
                <div>
                  <Label className="text-sm mb-2 block">Jours de la semaine</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {WEEK_DAYS.map(day => (
                      <Button
                        key={day}
                        variant={availabilityFilters.selectedWeekDays.includes(day) ? 'default' : 'outline'}
                        size="sm"
                        className="text-xs"
                        onClick={() => setAvailabilityFilters(prev => ({
                          ...prev,
                          selectedWeekDays: prev.selectedWeekDays.includes(day)
                            ? prev.selectedWeekDays.filter(d => d !== day)
                            : [...prev.selectedWeekDays, day],
                        }))}
                      >
                        {WEEK_DAY_LABELS[day]}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Time of day */}
                <div>
                  <Label className="text-sm mb-2 block">Moment de la journée</Label>
                  <Select
                    value={availabilityFilters.timeOfDay}
                    onValueChange={(value: 'all' | 'morning' | 'afternoon' | 'evening') => 
                      setAvailabilityFilters(prev => ({ ...prev, timeOfDay: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toute la journée</SelectItem>
                      <SelectItem value="morning">Matin (avant 12h)</SelectItem>
                      <SelectItem value="afternoon">Après-midi (12h-18h)</SelectItem>
                      <SelectItem value="evening">Soir (après 18h)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Reset button */}
                {hasAvailabilityFilter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => setAvailabilityFilters({ selectedDates: [], selectedWeekDays: [], timeOfDay: 'all' })}
                  >
                    Réinitialiser les filtres de disponibilité
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Results */}
        <div className="space-y-3">
          {mediatorsLoading && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
              <p className="text-sm text-muted-foreground">Recherche de médiateurs...</p>
            </div>
          )}

          {!mediatorsLoading && totalCount === 0 && (
            <div className="text-center py-8">
              <User className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Aucun médiateur trouvé</p>
              <Button 
                variant="link" 
                size="sm"
                onClick={() => {
                  setSearchFilters({ city: '', mediationArea: '' });
                  setAvailabilityFilters({ selectedDates: [], selectedWeekDays: [], timeOfDay: 'all' });
                  setOnlyWithSlots(false);
                }}
                className="mt-2"
              >
                Réinitialiser les filtres
              </Button>
              {!user && (
                <Button
                  onClick={() => navigate('/connexion?mode=signup')}
                  className="mt-3 w-full"
                >
                  Inscrivez-vous pour plus de résultats
                </Button>
              )}
            </div>
          )}

          {!mediatorsLoading && totalCount > 0 && (
            <>
              <div className="max-h-96 overflow-y-auto space-y-3 pr-2">
                {displayedMediators.map((mediator) => (
                  <MediatorResultCard
                    key={mediator.id}
                    mediator={mediator as any}
                    isAuthenticated={!!user}
                    isLoadingSlots={slotsLoading && !mediatorsWithSlots}
                    trackingSource="search_widget"
                    hasFutureSlots={
                      !mediatorsWithSlots || !mediator.id
                        ? true
                        : mediatorsWithSlots.has(mediator.id)
                    }
                  />
                ))}
              </div>

              {/* Sign up CTA for non-authenticated users */}
              {!user && mediators && mediators.length > displayLimit && (
                <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
                  <CardContent className="p-4 text-center">
                    <Lock className="h-8 w-8 mx-auto mb-2 text-primary" />
                    <p className="font-semibold text-foreground mb-1">
                      +{mediators.length - displayLimit} autres médiateurs disponibles
                    </p>
                    <p className="text-sm text-muted-foreground mb-3">
                      Inscrivez-vous pour voir tous les profils et prendre rendez-vous
                    </p>
                    <Button
                      onClick={() => navigate('/connexion?mode=signup')}
                      className="w-full"
                    >
                      Inscrivez-vous pour continuer
                    </Button>
                  </CardContent>
                </Card>
              )}

              {user && totalCount > displayLimit && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/trouver-un-mediateur')}
                  className="w-full"
                >
                  Voir tous les médiateurs ({totalCount})
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
